'use client';

import { c as _c } from "react/compiler-runtime";
import { type ReactNode, useState } from 'react';
import { cn } from '@/utils/cn';

/**
 * RecThumbnail — the program-card thumbnail panels on the exit screen: a full-height panel for the
 * best-match card, and a short photo band (with an overlapping company logo chip) for the two
 * vertical cards.
 *
 * The per-program hero comes from `LabsRecommendation.image` (the live catalog's artwork);
 * {@link REC_IMAGES} only backstops a missing or broken one. Images render via plain <img> — the app
 * uses no next/image, matching the <SubjectLogo> pattern.
 *
 * RUNTIME backstops: a hero that 404s / fails to load swaps ONCE to the local placeholder (guarded on
 * the src itself, so even a missing backstop cannot loop); a logo chip that fails to load hides
 * itself rather than showing a broken-image box.
 *
 * The accent gradient is a low-cost backdrop behind the image (it shows while the image loads and
 * tints the panel edges), themed by the ARCHETYPE accent from the pack. Everything else is fs-*
 * tokens. Imported by direct path from `recommendations.tsx` — NOT via the `_components` barrel.
 */

/**
 * Local artwork the exit screen falls back to. `best` also doubles as the runtime onError backstop
 * for ANY hero, so it must be an asset that is guaranteed to exist in `public/`.
 */
export const REC_IMAGES = {
  best: '/images/rec/best-match.png',
  cards: ['/images/rec/card-1.png', '/images/rec/card-2.png'] as const
} as const;

/** Accent at low alpha — the per-archetype wash (the same helper the post-M2 screens use). */
function accentTint(hex: string, alpha: number): string {
  const n = hex.replace('#', '');
  const r = Number.parseInt(n.slice(0, 2), 16);
  const g = Number.parseInt(n.slice(2, 4), 16);
  const b = Number.parseInt(n.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/** A soft two-stop diagonal accent gradient over the canvas — the behind-image backdrop. */
function accentGradient(hex: string): string {
  return `linear-gradient(135deg, ${accentTint(hex, 0.22)} 0%, ${accentTint(hex, 0.08)} 55%, ${accentTint(hex, 0.16)} 100%)`;
}

/**
 * LogoChip — the REAL company logo on a white rounded badge (object-contain). The white fill + border
 * + soft shadow keep it legible on BOTH the white details panel (best-match card) and the
 * photographic thumbnails (vertical cards). `size="lg"` is the overlapping vertical-card badge;
 * `size="md"` is the inline best-match badge.
 */
export function LogoChip(t0) {
  const $ = _c(14);
  const {
    logo,
    company,
    size: t1,
    className
  } = t0;
  const size = t1 === undefined ? "md" : t1;
  const [failed, setFailed] = useState(false);
  if (failed || !logo) {
    return null;
  }
  const t2 = size === "lg" ? "h-14" : "h-12";
  let t3;
  if ($[0] !== className || $[1] !== t2) {
    t3 = cn("inline-flex items-center justify-center rounded-fs-card border border-fs-border bg-fs-surface px-3 shadow-sm", t2, className);
    $[0] = className;
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const t4 = `${company} logo`;
  const t5 = size === "lg" ? "h-9" : "h-8";
  let t6;
  if ($[3] !== t5) {
    t6 = cn("w-auto max-w-[150px] object-contain", t5);
    $[3] = t5;
    $[4] = t6;
  } else {
    t6 = $[4];
  }
  const t7 = size === "lg" ? 36 : 32;
  let t8;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => setFailed(true);
    $[5] = t8;
  } else {
    t8 = $[5];
  }
  let t9;
  if ($[6] !== logo || $[7] !== t4 || $[8] !== t6 || $[9] !== t7) {
    t9 = <img alt={t4} className={t6} decoding="async" height={t7} loading="lazy" onError={t8} src={logo} width={150} />;
    $[6] = logo;
    $[7] = t4;
    $[8] = t6;
    $[9] = t7;
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  let t10;
  if ($[11] !== t3 || $[12] !== t9) {
    t10 = <span className={t3}>{t9}</span>;
    $[11] = t3;
    $[12] = t9;
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  return t10;
}

/**
 * Best-match (large) variant — full-height panel with the program photo filling it. Used as the LEFT
 * half of the horizontal best-match card.
 */
export function PrimaryThumbnail(t0) {
  const $ = _c(14);
  const {
    src,
    company,
    accent,
    className
  } = t0;
  const [imgSrc, setImgSrc] = useState(src);
  let t1;
  if ($[0] !== className) {
    t1 = cn("relative overflow-hidden", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== accent) {
    t2 = accentGradient(accent);
    $[2] = accent;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t2) {
    t3 = {
      background: t2
    };
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const t4 = `${company} program thumbnail`;
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => setImgSrc(_temp);
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== imgSrc || $[8] !== t4) {
    t6 = <img alt={t4} className="absolute inset-0 h-full w-full object-cover" decoding="async" height={400} loading="lazy" onError={t5} src={imgSrc} width={530} />;
    $[7] = imgSrc;
    $[8] = t4;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t1 || $[11] !== t3 || $[12] !== t6) {
    t7 = <div aria-hidden={true} className={t1} style={t3}>{t6}</div>;
    $[10] = t1;
    $[11] = t3;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  return t7;
}

/**
 * Vertical variant — short photo band with an overlapping company logo chip straddling its bottom
 * edge. Used as the top band of the two vertical program cards.
 */
function _temp(current) {
  return current === REC_IMAGES.best ? current : REC_IMAGES.best;
}
export function VerticalThumbnail(t0) {
  const $ = _c(19);
  const {
    src,
    company,
    logo,
    accent,
    className
  } = t0;
  const [imgSrc, setImgSrc] = useState(src);
  let t1;
  if ($[0] !== className) {
    t1 = cn("relative h-[210px] w-full", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== accent) {
    t2 = accentGradient(accent);
    $[2] = accent;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t2) {
    t3 = {
      background: t2
    };
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => setImgSrc(_temp2);
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== imgSrc) {
    t5 = <img alt="" className="absolute inset-0 h-full w-full object-cover" decoding="async" height={210} loading="lazy" onError={t4} src={imgSrc} width={510} />;
    $[7] = imgSrc;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t3 || $[10] !== t5) {
    t6 = <div aria-hidden={true} className="absolute inset-0 overflow-hidden" style={t3}>{t5}</div>;
    $[9] = t3;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== company || $[13] !== logo) {
    t7 = <div className="absolute -bottom-7 left-6"><LogoChip company={company} logo={logo} size="lg" /></div>;
    $[12] = company;
    $[13] = logo;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== t1 || $[16] !== t6 || $[17] !== t7) {
    t8 = <div className={t1}>{t6}{t7}</div>;
    $[15] = t1;
    $[16] = t6;
    $[17] = t7;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  return t8;
}
function _temp2(current) {
  return current === REC_IMAGES.best ? current : REC_IMAGES.best;
}